import React from 'react';
import { Button, Divider, ProgressBar, Skeleton, Text, TruncatedText } from '@spark';
import { formatScore } from '@/shared/utils/metricFormatting';

const MONO_FONT = "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace";

/** Dict match table scrolls once it holds more than this many keys. */
const MAX_VISIBLE_DICT_FIELDS = 5;
/** Approximate row height (12px vertical padding + one line of text). */
const DICT_ROW_HEIGHT = 45;

const ScoreValue = ({ value }) => {
  const formatted = formatScore(value, { decimals: 3 });
  if (formatted === null) {
    return <Text variant="base-regular" color="secondary">-</Text>;
  }
  return <Text variant="base-regular" color="primary">{formatted}</Text>;
};

/** Model name that ellipsizes on one line and shows the full name on hover. */
const ModelLabel = ({ label, maxWidth = 140 }) => (
  <TruncatedText text={label} maxWidth={maxWidth}>
    <Text variant="detail-regular" color="tertiary">{label}</Text>
  </TruncatedText>
);

/** Right side of a metric header: single number, or labeled comparison + current numbers. */
const MetricValues = ({ metric, showTeacher, teacherLabel, studentLabel, teacherLoading }) => {
  if (!showTeacher) {
    return <ScoreValue value={metric?.student} />;
  }
  const column = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, minWidth: 0 };
  return (
    <div style={{ display: 'flex', gap: 24 }}>
      <div style={column}>
        <ModelLabel label={teacherLabel} />
        {metric?.teacher == null && teacherLoading ? (
          <Text variant="base-regular" color="secondary">In progress</Text>
        ) : (
          <ScoreValue value={metric?.teacher} />
        )}
      </div>
      <div style={column}>
        <ModelLabel label={studentLabel} />
        <ScoreValue value={metric?.student} />
      </div>
    </div>
  );
};

const MetricBlock = ({ title, titleSuffix, description, metric, showTeacher, teacherLabel, studentLabel, teacherLoading, children }) => (
  <div style={{ padding: '16px 24px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <Text variant="base-semibold" style={{ fontSize: 16 }}>{title}</Text>
          {titleSuffix ? (
            <Text variant="base-regular" color="secondary" style={{ fontSize: 15 }}>{titleSuffix}</Text>
          ) : null}
        </div>
        <div style={{ marginTop: 4 }}>
          <Text variant="base-regular" color="secondary">{description}</Text>
        </div>
      </div>
      <MetricValues
        metric={metric}
        showTeacher={showTeacher}
        teacherLabel={teacherLabel}
        studentLabel={studentLabel}
        teacherLoading={teacherLoading}
      />
    </div>
    {children}
  </div>
);

const DictMatchFieldsTable = ({ fields, showTeacher, teacherLabel, studentLabel, teacherLoading }) => {
  const gridTemplateColumns = showTeacher ? '1fr 120px 180px' : '1fr 180px';

  const headerCaption = (caption) => (
    <Text variant="uniq-semibold-table-caps" color="tertiary">{caption}</Text>
  );

  /** Right-aligned header cell; long model names ellipsize with hover tooltip. */
  const modelHeaderCell = (label) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
      <TruncatedText text={label} maxWidth="100%">
        {headerCaption(label)}
      </TruncatedText>
    </div>
  );

  const teacherCell = (value) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      {value == null && teacherLoading ? (
        <Text variant="detail-regular" color="secondary">In progress</Text>
      ) : (
        <ScoreValue value={value} />
      )}
    </div>
  );

  const studentCell = (value) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 90, display: 'inline-flex' }}>
        <ProgressBar value={(Number(value) || 0) * 100} showPercentage={false} />
      </div>
      <ScoreValue value={value} />
    </div>
  );

  const scrollable = fields.length > MAX_VISIBLE_DICT_FIELDS;

  return (
    <div style={{ marginTop: 16, border: '1px solid #f3f4f6', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns, gap: 12, padding: '10px 16px', background: '#f9fafb', alignItems: 'center' }}>
        <div>{headerCaption('Key')}</div>
        {showTeacher ? modelHeaderCell(teacherLabel) : null}
        {showTeacher ? modelHeaderCell(studentLabel) : <div style={{ textAlign: 'right' }}>{headerCaption('Score')}</div>}
      </div>
      <div
        style={scrollable ? { maxHeight: DICT_ROW_HEIGHT * MAX_VISIBLE_DICT_FIELDS, overflowY: 'auto' } : undefined}
        data-testid="dict-match-rows"
      >
        {fields.map((row) => (
          <div
            key={row.field}
            style={{ display: 'grid', gridTemplateColumns, gap: 12, padding: '12px 16px', borderTop: '1px solid #f3f4f6', alignItems: 'center' }}
          >
            <span style={{ fontFamily: MONO_FONT, fontSize: 14, color: '#101828', overflowWrap: 'anywhere' }}>{row.field}</span>
            {showTeacher ? teacherCell(row.teacher) : null}
            {studentCell(row.student)}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * "Accuracy Metrics" card (Evaluations Overview, content generation tasks
 * only): Exact Match, Fuzzy Similarity (MinHash), and Dict Match (with
 * per-field breakdown) displayed as absolute numbers instead of the Classic
 * Metrics bar chart. Header matches the LLM as a Judge / Classic Metrics
 * cards, with a Configure button (`onConfigure`) opening the accuracy-scope
 * metrics config modal.
 */
const ContentGenerationAccuracy = ({
  data,
  teacherLabel = 'Teacher',
  studentLabel = 'Specific AI',
  showComparison = true,
  loading = false,
  teacherLoading = false,
  onConfigure,
}) => {
  const { exactMatch, fuzzySimilarity, dictMatch } = data || {};

  const hasAnyTeacherValue =
    exactMatch?.teacher != null || fuzzySimilarity?.teacher != null || dictMatch?.teacher != null;
  const showTeacher = showComparison && (hasAnyTeacherValue || teacherLoading);

  const sharedProps = { showTeacher, teacherLabel, studentLabel, teacherLoading };

  const blocks = [];
  if (exactMatch) {
    blocks.push(
      <MetricBlock
        key="exact-match"
        title="Accuracy"
        titleSuffix="- Full Output (Exact Match)"
        description="Checks whether the model output exactly matches the expected output."
        metric={exactMatch}
        {...sharedProps}
      />,
    );
  }
  if (fuzzySimilarity) {
    blocks.push(
      <MetricBlock
        key="fuzzy-similarity"
        title="Fuzzy Similarity"
        titleSuffix="- Full Output (MinHash)"
        description="Fuzzy shingle-based similarity against the expected output."
        metric={fuzzySimilarity}
        {...sharedProps}
      />,
    );
  }
  if (dictMatch) {
    blocks.push(
      <MetricBlock
        key="dict-match"
        title="Dict Match"
        description="Checks whether each field in a structured output matches the expected value."
        metric={dictMatch}
        {...sharedProps}
      >
        <DictMatchFieldsTable fields={dictMatch.fields} {...sharedProps} />
      </MetricBlock>,
    );
  }

  return (
    <div className="fs-unmask" style={{ borderRadius: 8, border: '1px solid #e0e0e0', background: '#fff' }}>
      <div style={{ padding: '16px 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Text variant="base-semibold" style={{ fontWeight: 600, fontSize: 16, display: 'block' }}>
            Accuracy Metrics
          </Text>
          {onConfigure ? (
            <Button
              variant="link"
              size="small"
              onClick={loading ? undefined : onConfigure}
            >
              Configure
            </Button>
          ) : null}
        </div>
      </div>
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 24 }}>
          <Skeleton shapeType="paragraph" width="100%" />
          <Skeleton shapeType="paragraph" width="60%" />
        </div>
      ) : blocks.length === 0 ? (
        <div style={{ padding: '12px 24px 24px' }}>
          <Text variant="base-regular" color="secondary">
            No accuracy metrics enabled. Use Configure to choose which metrics are displayed.
          </Text>
        </div>
      ) : (
        blocks.map((block, index) => (
          <React.Fragment key={block.key}>
            {index > 0 ? <Divider /> : null}
            {block}
          </React.Fragment>
        ))
      )}
    </div>
  );
};

export default ContentGenerationAccuracy;
